<template>
  <Transition name="keyboard-slide">
    <div v-if="show" v-click-outside="closeKeyboard" class="keyboard" :class="[lang === 'rus' ? 'rus' : 'en', number ? 'number' : '']">
      <div v-for="(row, index) in keys[lang]" :key="index" class="row" :class="`row_${index}`">
        <div
          v-for="key in row" :key="key" class="keyboard__key" :class="key" @click="clickHandler(key)"
          @mousedown.stop
        >
          <span v-if="key === 'clear' && lang === 'rus'">
            Очистить
          </span>
          <span v-else-if="key === 'clear' && lang === 'en'">
            Clear
          </span>
          <span v-else-if="key === 'remove'">
            <Icons variant="remove" />
          </span>
          <span v-else-if="key === 'space'">
            <Icons variant="space" />
          </span>
          <span v-else-if="key === 'lang' && lang === 'rus'">
            ABC
          </span>
          <span v-else-if="key === 'lang' && lang === 'en'">
            АБВ
          </span>
          <span v-else>
            {{ key }}
          </span>
        </div>
      </div>
    </div>
  </Transition>
</template>
<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Icons from '@/components/UI/Icons/';

type Lang = 'rus' | 'en';

interface KeyMap {
    rus: string[][]
    en: string[][]
}

const props = defineProps<{
    modelValue?: string
    number?: boolean
    focus?: boolean
    autoShow?: boolean
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
    (e: 'clear'): void
}>();

const { locale } = useI18n();

const show = ref(false);
const lang = ref<Lang>('rus');
const ignoreClickOutside = ref(false);

const baseKeys = (): KeyMap => ({
    rus: [
        ['й', 'ц', 'у', 'к', 'е', 'н', 'г', 'ш', 'щ', 'з', 'х', 'remove'],
        ['ф', 'ы', 'в', 'а', 'п', 'р', 'о', 'л', 'д', 'ж', 'э', 'space'],
        ['я', 'ч', 'с', 'м', 'и', 'т', 'ь', 'б', 'ю', '_', 'lang']
    ],
    en: [
        ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'remove'],
        ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'space'],
        ['z', 'x', 'c', 'v', 'b', 'n', 'm', '_', 'lang']
    ]
});

const keys = ref<KeyMap>(baseKeys());

watch(show, (newValue) => {
    if (newValue) {
        lang.value = locale.value === 'ru' ? 'rus' : 'en';
    }
});

onMounted(() => {
    if (props.autoShow) {
        showKeyboard();
    }
});

function showKeyboard() {
    if (props.number) {
        const numberRow = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'clear'];
        (['rus', 'en'] as Lang[]).forEach((l) => {
            const hasNumberRow = keys.value[l].some(row =>
                row.length === numberRow.length && row.every((key, i) => key === numberRow[i])
            );
            if (!hasNumberRow) {
                keys.value[l].unshift(numberRow);
            }
        });
    }

    show.value = true;
}

function closeKeyboard() {
    if (!props.focus && !props.autoShow) {
        if (show.value && !ignoreClickOutside.value) {
            show.value = false;
            keys.value = baseKeys();
        }
    }
}

function clickHandler(key: string) {
    let value = props.modelValue || '';
    switch (key) {
        case 'remove':
            value = value.slice(0, -1);
            break;
        case 'space':
            value += ' ';
            break;
        case 'clear':
            value = '';
            emit('clear');
            break;
        case 'lang':
            ignoreClickOutside.value = true;
            lang.value = lang.value === 'rus' ? 'en' : 'rus';
            nextTick(() => {
                setTimeout(() => {
                    ignoreClickOutside.value = false;
                }, 0);
            });
            return;
        default:
            if (key.length === 1 && !Number(key)) {
                value += key.toUpperCase();
            } else {
                value += key;
            }
    }
    emit('update:modelValue', value);
}

defineExpose({ showKeyboard, closeKeyboard });
</script>
<style scoped lang="scss">
.keyboard {
    padding: 37px 34px 68px 34px;
    position: fixed;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    background: var(--color-white);
    z-index: 9999;
    box-shadow: 0px 0px 30px 0px #0000000D;

    &.rus {
        & .row {
            display: grid;
            grid-template-columns: repeat(14, minmax(60px, 1fr));
            grid-column-gap: 4px;
        }
    }

    &.en {
        & .row {
            display: grid;
            grid-template-columns: repeat(13, minmax(60px, 1fr));
            grid-column-gap: 4px;

            &.row_1 {
                grid-template-columns: repeat(12, minmax(60px, 1fr));
            }

            &.row_2 {
                grid-template-columns: repeat(12, minmax(60px, 1fr));
            }
        }
        &.number {
            & .row {
                grid-template-columns: repeat(14, minmax(60px, 1fr));
                &.row_1 {
                    grid-template-columns: repeat(13, minmax(60px, 1fr));
                }
                &.row_2 {
                    grid-template-columns: repeat(12, minmax(60px, 1fr));
                }
                &.row_3 {
                    grid-template-columns: repeat(12, minmax(60px, 1fr));
                }
            }
        }
    }

    &__key {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 60px;
        font-size: 22px;
        font-weight: 700;
        color: var(--color-green);
        text-transform: uppercase;
        background: var(--color-white);
        box-shadow: 1px 3px 25px 0px #002D1D1A;
        border-radius: 10px;

        &:active {
            background: var(--color-light-green);
        }

        &.clear,
        &.lang {
            grid-column: span 4;
            text-transform: capitalize;
            background: var(--color-light-green);

            &:active {
                color: var(--color-white);
                background: var(--color-green);
            }
        }

        &.remove,
        &.space {
            grid-column: span 3;
            background: var(--color-light-green);

            &:active {
                color: var(--color-white);
                background: var(--color-green);
            }
        }
    }
}

.keyboard-slide-enter-active,
.keyboard-slide-leave-active {
    transition: transform 0.3s ease;
}

.keyboard-slide-enter-from,
.keyboard-slide-leave-to {
    transform: translateY(100%);
}
</style>